import {
  Directive,
  EffectRef,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';

/**
 * Usage
 * <div outsideClick (clickedOutside)="onClose()">...</div>
 *
 * - Apply the directive with the `outsideClick` attribute.
 * - Bind to the `(clickedOutside)` output to react when a click happens outside the host element.
 * - Optionally toggle with `[outsideClickActive]`.
 */
@Directive({
  selector: '[outsideClick]',
  standalone: true,
})
export class OutsideClickDirective implements OnInit, OnDestroy {
  #host = inject(ElementRef<HTMLElement>);
  #renderer = inject(Renderer2);
  #zone = inject(NgZone);

  /** Enable/disable outside click detection (signal) */
  outsideClickActive = input<boolean>(true);
  #ignoreUntil = 0; // timestamp (ms) until which outside clicks are ignored
  /** Selectors to ignore (treat as inside) when checking outside clicks */

  /** How long to suppress outside clicks after enabling (ms) */
  clickTimeout = input<number>(200);

  // Accept only string selectors array (or undefined/null)
  outsideClickIgnore = input<string[] | null | undefined>(undefined);

  // Normalize ignore list and merge with base defaults
  #ignoreListComputed = computed<string[]>(() => {
    const list = this.outsideClickIgnore();
    return Array.isArray(list) ? (list.filter(Boolean) as string[]) : [];
  });

  /** Emits when a click occurs outside the host element */
  clickedOutside = output<void>();

  #removeClickListener?: () => void;
  #removeMouseDownListener?: () => void;
  #removeTouchListener?: () => void;

  // Track transitions false -> true to start suppression window
  #prevActive: boolean = true;
  #initialized = false;
  #activeEffect: EffectRef = effect(() => {
    const active = this.outsideClickActive();
    const timeout = this.clickTimeout();
    // Start suppression window if active (either on toggle or on first init)
    if (active && timeout > 0) {
      if (!this.#initialized || !this.#prevActive) {
        this.#ignoreUntil = Date.now() + timeout;
      }
    }
    this.#prevActive = active;
    this.#initialized = true;
  });

  ngOnInit(): void {
    // Register global listeners outside Angular for performance
    this.#zone.runOutsideAngular(() => {
      this.#removeClickListener = this.#renderer.listen('document', 'click', (event: Event) =>
        this._handleDocumentEvent(event),
      );
      // Handle mousedown to close on click-and-hold outside
      this.#removeMouseDownListener = this.#renderer.listen('document', 'mousedown', (event: Event) =>
        this._handleDocumentEvent(event),
      );
      // Handle touchstart to support mobile interactions (fires earlier than click)
      this.#removeTouchListener = this.#renderer.listen('document', 'touchstart', (event: Event) =>
        this._handleDocumentEvent(event),
      );
    });
  }

  ngOnDestroy(): void {
    if (this.#removeClickListener) {
      this.#removeClickListener();
      this.#removeClickListener = undefined;
    }
    if (this.#removeMouseDownListener) {
      this.#removeMouseDownListener();
      this.#removeMouseDownListener = undefined;
    }
    if (this.#removeTouchListener) {
      this.#removeTouchListener();
      this.#removeTouchListener = undefined;
    }
    // Dispose reactive effect
    this.#activeEffect?.destroy();
  }

  private _handleDocumentEvent(event: Event) {
    const target = event.target as Node | null;
    const hostEl = this.#host.nativeElement;

    // If event target is inside the host element, or removed from DOM, ignore
    if (!target || !document.contains(target) || hostEl.contains(target)) {
      return;
    }

    // If event target is inside a CDK overlay pane that does NOT contain the host,
    // ignore (e.g. mat-menu, mat-select panel opened from elsewhere).
    // If the host itself is inside an overlay pane (e.g. inside a dialog), clicks
    // within that same pane should still be processed.
    const hostPane = (hostEl as HTMLElement).closest('.cdk-overlay-pane');
    const overlayPanes = document.querySelectorAll('.cdk-overlay-pane');
    if (Array.from(overlayPanes).some((pane) => pane.contains(target) && pane !== hostPane)) {
      return;
    }

    // If target is inside any ignored element, or matches any ignored selector, ignore
    if (this.#isIgnoredTarget(target)) {
      return;
    }

    // If within the suppression window, ignore
    if (this.#ignoreUntil && Date.now() < this.#ignoreUntil) {
      return;
    }

    // Emit inside Angular zone so change detection runs in consumers
    this.#zone.run(() => {
      if (!this.outsideClickActive()) return;
      this.clickedOutside.emit();
    });
  }

  #isIgnoredTarget(target: Node): boolean {
    const ignoreList = this.#ignoreListComputed();
    if (!ignoreList || ignoreList.length === 0) return false;
    const asElement = target as Node as Element;
    for (const selector of ignoreList) {
      try {
        if (asElement instanceof Element && asElement.closest(selector)) {
          return true;
        }
      } catch {}
    }
    return false;
  }
}
